import React from "react";
import { cn } from "@shared/lib/utils";

export interface QuickActionItem {
  title: string;
  description: string;
  icon: React.ElementType; // ✅ Accepts Lucide icons and any React component
  color: string; // Tailwind classes for background/hover
  onClick: () => void;
}

interface QuickActionsProps {
  actions: QuickActionItem[];
  heading?: string;
  subheading?: string;
}

export const QuickActions: React.FC<QuickActionsProps> = ({
  actions,
  heading = "Quick Actions",
  subheading = "Streamline common tasks with one-click actions",
}) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="h-1" />
      <div className="p-3">
        <div className="mb-3">
          <h2 className="text-sm font-bold text-gray-900">{heading}</h2>
          <p className="text-xs text-gray-600">{subheading}</p>
        </div>

        <div className={`grid grid-cols-${actions.length} gap-2`}>
          {actions.map((action, index) => {
            const Icon = action.icon; // ✅
            return (
              <button
                key={index}
                onClick={action.onClick}
                className={cn(
                  action.color,
                  "text-gray-800 p-3 rounded-lg transition-all duration-200 hover:scale-105 hover:shadow-lg group focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                )}
              >
                <div className="flex items-center gap-3">
                  <Icon
                    size={18}
                    className="flex-shrink-0 text-gray-700 group-hover:scale-110 transition-transform duration-200"
                  />
                  <div className="text-left flex-1">
                    <div className="font-semibold text-xs leading-tight">
                      {action.title}
                    </div>
                    <div className="text-xs text-gray-600 leading-tight">
                      {action.description}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
